import type { ApiError } from '@empathyco/playboard-core-domain'
import type { IndexJobDeploymentDTO, IndexJobDTO, IndexJobTransformationDTO } from '../models'
import type { IndexJobAdapter } from './interfaces'
import { AdapterDateUtils } from '@empathyco/playboard-core-domain'
import { injectable } from 'inversify'

export interface PlatformIndexJobEntity {
  correlationId: string
  clientId: string
  state: PlatformIndexJobState
  transformation: PlatformIndexJobTransformation
  deployments: Array<PlatformIndexJobDeployment>
  metrics: {
    totalTime: number | string
    totalTimePretty?: string
  }
}

export type PlatformIndexJobState = 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'INCOMPLETE'

interface PlatformIndexJobTransformation {
  indexJobId: string
  state: PlatformIndexJobTransformationState
  feedId: string
  date: string
  updatedAt?: string
  updateAt?: string
  errors?: Array<string>
  metrics: {
    docsWritten: number
    docsRead: number
  }
}

type PlatformIndexJobTransformationState = PlatformIndexJobDeploymentState | 'DISCARDED'

type PlatformIndexJobDeploymentState = 'QUEUED' | 'RUNNING' | 'SUCCESS' | 'FAILURE'

interface PlatformIndexJobDeployment {
  deploymentId: string
  packageUri: string
  pivots?: Record<string, string>
  state: PlatformIndexJobDeploymentState
  date: string
  updatedAt: string
  errors: Array<string>
}

export interface PlatformIndexJobError {
  status: number
  data?: any
}

@injectable()
export class PlatformIndexJobAdapter implements IndexJobAdapter<
  PlatformIndexJobEntity,
  PlatformIndexJobError
> {
  decode(payload: PlatformIndexJobEntity): IndexJobDTO {
    const { correlationId, clientId, state, transformation, deployments, metrics } = payload ?? {}
    return {
      id: transformation?.indexJobId,
      correlationId,
      clientId,
      state,
      transformation: this.decodeTransformation(transformation),
      deployments: deployments?.map(deployment => this.decodeDeployment(deployment)) ?? [],
      metrics: {
        totalTime: metrics?.totalTimePretty ?? metrics?.totalTime,
      },
    }
  }

  decodeError(error: PlatformIndexJobError): ApiError {
    return {
      status: error.status,
    }
  }

  private decodeTransformation(
    transformation: PlatformIndexJobTransformation,
  ): IndexJobTransformationDTO {
    const { state, feedId, date, updatedAt, updateAt, errors, metrics } = transformation ?? {}
    return {
      state,
      feedId,
      created: AdapterDateUtils.decodeDate(date)!,
      updated: AdapterDateUtils.decodeDate(updatedAt ?? updateAt)!,
      errors: errors ?? [],
      metrics: {
        docsWritten: metrics?.docsWritten,
        docsRead: metrics?.docsRead,
      },
    }
  }

  private decodeDeployment(deployment: PlatformIndexJobDeployment): IndexJobDeploymentDTO {
    const { deploymentId, packageUri, pivots, state, date, updatedAt, errors } = deployment
    return {
      id: deploymentId,
      packageUri,
      pivots: pivots ?? {},
      state,
      created: AdapterDateUtils.decodeDate(date)!,
      updated: AdapterDateUtils.decodeDate(updatedAt)!,
      errors: errors ?? [],
    }
  }
}
